import type {
  CreateCredentialInput,
  UpdateCredentialInput,
  CredentialSummaryResponse,
} from '@erp/rpc';

const CREDENTIAL_TYPE_VALUES = [
  'password',
  'api_token',
  'oauth_client',
  'certificate',
  'azure_blob',
  'aws',
] as const;

export type CredentialTypeValue = (typeof CREDENTIAL_TYPE_VALUES)[number];

export function isCredentialTypeValue(v: string): v is CredentialTypeValue {
  return (CREDENTIAL_TYPE_VALUES as readonly string[]).includes(v);
}

interface FormValues {
  organizationId: string;
  credential?: CredentialSummaryResponse;
  type: CreateCredentialInput['type'];
  name: string;
  username: string;
  accessKeyId: string;
  secret: string;
  autoPauseAfterRejectedRuns: number | null;
}

function formMetadata(values: FormValues): Record<string, unknown> {
  const metadata: Record<string, unknown> = {};
  if (values.type === 'password' && values.username.trim())
    metadata.username = values.username.trim();
  if (values.type === 'aws' && values.accessKeyId.trim())
    metadata.accessKeyId = values.accessKeyId.trim();
  return metadata;
}

export function buildCredentialFormSubmission(
  values: FormValues,
):
  | { mode: 'edit'; input: UpdateCredentialInput }
  | { mode: 'create'; input: CreateCredentialInput } {
  const metadata = formMetadata(values);
  const { credential, autoPauseAfterRejectedRuns } = values;
  const common = {
    organizationId: values.organizationId,
    name: values.name.trim(),
  };
  if (credential)
    return {
      mode: 'edit',
      input: {
        ...common,
        credentialId: credential.id,
        autoPauseAfterRejectedRuns:
          autoPauseAfterRejectedRuns !== credential.autoPauseAfterRejectedRuns
            ? autoPauseAfterRejectedRuns
            : undefined,
        secret: values.secret.trim() || undefined,
        metadataPatch: Object.entries(metadata).some(
          ([key, value]) => value !== credential.metadata?.[key],
        )
          ? metadata
          : undefined,
      },
    };
  return {
    mode: 'create',
    input: {
      ...common,
      type: values.type,
      autoPauseAfterRejectedRuns,
      secret: values.secret.trim(),
      metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
    },
  };
}
